(() => {
  const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
  const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024;
  const MAX_OUTPUT_EDGE = 1600;
  const MIN_CROP_SIZE = 0.05;
  const JPEG_QUALITY = 0.9;
  const createDefaultCrop = () => ({ x: 0.1, y: 0.1, width: 0.8, height: 0.8 });
  const uploadStates = new Map();
  const baseColorStates = new Map();
  let instagramScriptPromise;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const getUploadState = (key) => {
    if (!uploadStates.has(key)) {
      uploadStates.set(key, {
        slots: Array.from({ length: 4 }, () => ({
          original: null,
          cropped: null,
          previewUrl: '',
          status: 'empty',
          error: '',
          crop: createDefaultCrop(),
        })),
      });
    }

    return uploadStates.get(key);
  };

  const revokeUrl = (url) => {
    if (url) URL.revokeObjectURL(url);
  };

  const marketingAllowed = () => {
    const privacy = window.Shopify?.customerPrivacy;
    return Boolean(privacy && typeof privacy.marketingAllowed === 'function' && privacy.marketingAllowed());
  };

  const loadInstagram = () => {
    if (window.instgrm?.Embeds) return Promise.resolve();
    if (instagramScriptPromise) return instagramScriptPromise;

    instagramScriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[src="https://www.instagram.com/embed.js"]');
      const script = existing || document.createElement('script');

      const handleLoad = () => resolve();
      const handleError = () => reject(new Error('Instagram could not be loaded.'));

      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });

      if (!existing) {
        script.async = true;
        script.defer = true;
        script.src = 'https://www.instagram.com/embed.js';
        document.body.append(script);
      }
    });

    return instagramScriptPromise;
  };

  class BrCreateeReels extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      this.addEventListener('click', this.handleClick, { signal: this.abortController.signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }

    setStatus(message) {
      const status = this.querySelector('[data-reels-status]');
      if (status) status.textContent = message;
    }

    handleClick = async (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-reel-load]') : null;
      if (!button || !this.contains(button)) return;

      const card = button.closest('.br-createe-reels__card');
      const fallback = card?.querySelector('[data-reel-fallback]');
      const target = card?.querySelector('[data-reel-target]');
      const reelUrl = button.dataset.reelUrl || '';

      if (!marketingAllowed()) {
        button.hidden = true;
        if (fallback) fallback.hidden = false;
        this.setStatus('Instagram is available through an external link with your current privacy choices.');
        fallback?.focus();
        return;
      }

      let parsedUrl;
      try {
        parsedUrl = new URL(reelUrl);
      } catch (_error) {
        parsedUrl = null;
      }

      if (!parsedUrl || parsedUrl.protocol !== 'https:' || parsedUrl.hostname !== 'www.instagram.com' || !parsedUrl.pathname.startsWith('/reel/')) {
        this.setStatus('This Reel URL is not valid.');
        return;
      }

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      this.setStatus('Loading the selected Reel.');

      const blockquote = document.createElement('blockquote');
      blockquote.className = 'instagram-media';
      blockquote.dataset.instgrmPermalink = parsedUrl.href;
      blockquote.dataset.instgrmVersion = '14';
      const link = document.createElement('a');
      link.href = parsedUrl.href;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = 'View on Instagram';
      blockquote.append(link);

      target.replaceChildren(blockquote);
      target.hidden = false;

      try {
        await loadInstagram();
        window.instgrm?.Embeds?.process();
        button.hidden = true;
        this.setStatus('The selected Reel is ready.');
      } catch (_error) {
        target.hidden = true;
        fallback.hidden = false;
        button.hidden = true;
        this.setStatus('Instagram could not be loaded. Use the external link instead.');
        fallback.focus();
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    };
  }

  class BrCreateeBaseColor extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      this.stateKey = this.dataset.formId || 'createe-color';
      this.addEventListener('change', this.handleChoice, { signal: this.abortController.signal });
      this.addEventListener('input', this.handleInput, { signal: this.abortController.signal });
      this.syncControls();
      this.restoreState();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }

    syncControls() {
      this.propertyInput = this.querySelector('[data-base-color-property]');
      this.error = this.querySelector('[data-base-color-error]');
      this.customChoice = this.querySelector('[data-custom-choice]');
      this.customControls = this.querySelector('[data-custom-color-controls]');
      this.colorPicker = this.querySelector('[data-custom-color-picker]');
      this.hexInput = this.querySelector('[data-custom-color-hex]');
    }

    setError(message) {
      this.syncControls();
      if (this.error) this.error.textContent = message;
    }

    selectCustom() {
      this.syncControls();
      if (this.customChoice) this.customChoice.checked = true;
      this.setCustomControlsVisible(true);
    }

    setCustomControlsVisible(visible) {
      this.syncControls();
      if (this.customControls) this.customControls.hidden = !visible;
    }

    restoreState() {
      this.syncControls();
      const saved = baseColorStates.get(this.stateKey);
      if (!saved) return;
      const choice = Array.from(this.querySelectorAll('[data-base-color-choice]')).find((item) => {
        if (saved.custom) return item.dataset.customChoice !== undefined;
        return item.value === saved.value;
      });
      if (choice) choice.checked = true;
      this.setCustomControlsVisible(Boolean(saved.custom));
      if (saved.custom && saved.hex) {
        if (this.colorPicker) this.colorPicker.value = saved.hex;
        if (this.hexInput) this.hexInput.value = saved.hex;
      }
      this.setPropertyValue(saved.value);
    }

    normalizeHex(value) {
      const candidate = value.trim().toUpperCase();
      return /^#[0-9A-F]{6}$/.test(candidate) ? candidate : '';
    }

    setPropertyValue(value) {
      this.syncControls();
      if (!this.propertyInput) return;
      this.propertyInput.value = value;
      this.propertyInput.setAttribute('value', value);
    }

    getValue() {
      this.syncControls();
      const selected = this.querySelector('[data-base-color-choice]:checked');
      if (!(selected instanceof HTMLInputElement)) return '';
      if (selected.dataset.customChoice === undefined) return selected.value;
      const hex = this.normalizeHex(this.hexInput?.value || '');
      return hex ? `Custom (${hex})` : '';
    }

    commitCustom() {
      this.syncControls();
      const hex = this.normalizeHex(this.hexInput?.value || '');
      if (!hex) {
        this.setPropertyValue('');
        this.setError('Enter a valid HEX color in the format #RRGGBB.');
        return false;
      }

      if (this.colorPicker) this.colorPicker.value = hex;
      if (this.hexInput) this.hexInput.value = hex;
      this.setPropertyValue(`Custom (${hex})`);
      baseColorStates.set(this.stateKey, { custom: true, hex, value: `Custom (${hex})` });
      this.setError('');
      return true;
    }

    handleChoice = (event) => {
      const choice = event.target;
      if (!(choice instanceof HTMLInputElement) || !choice.matches('[data-base-color-choice]') || !choice.checked) return;
      this.syncControls();

      if (choice.dataset.customChoice !== undefined) {
        this.setCustomControlsVisible(true);
        this.commitCustom();
      } else {
        this.setCustomControlsVisible(false);
        this.setPropertyValue(choice.value);
        baseColorStates.set(this.stateKey, { custom: false, hex: '', value: choice.value });
        this.setError('');
      }
    };

    handleInput = (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (input.matches('[data-custom-color-picker]')) this.handlePicker();
      if (input.matches('[data-custom-color-hex]')) this.handleHex();
    };

    handlePicker = () => {
      this.syncControls();
      this.selectCustom();
      const hex = (this.colorPicker?.value || '').toUpperCase();
      if (this.hexInput) this.hexInput.value = hex;
      this.commitCustom();
    };

    handleHex = () => {
      this.syncControls();
      this.selectCustom();
      this.commitCustom();
    };

    validate() {
      this.syncControls();
      const selected = this.querySelector('[data-base-color-choice]:checked');
      const value = this.getValue();
      if (!selected || !value) {
        this.setError('Choose a recommended color or enter a valid custom color.');
        selected?.focus();
        return false;
      }

      if (selected.dataset.customChoice !== undefined) return this.commitCustom();
      this.setPropertyValue(value);
      this.setError('');
      return true;
    }
  }

  class BrCreateeDesignSummary extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.productRoot = this.closest('.product-information') || this.closest('.shopify-section') || document;
      this.variantData = this.readVariantData();
      this.styleMedia = this.readStyleMedia();
      this.currency = this.dataset.createeCurrency || window.Shopify?.currency?.active || 'USD';

      this.productRoot.addEventListener('change', this.handleVariantChange, {
        signal: this.abortController.signal,
      });
      this.productRoot.addEventListener('click', this.handleClick, {
        signal: this.abortController.signal,
      });

      this.observer = new MutationObserver(this.scheduleRefresh);
      this.observeProductRoot();
      this.refresh();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
      if (this.refreshFrame) cancelAnimationFrame(this.refreshFrame);
      this.abortController = null;
      this.observer = null;
      this.refreshFrame = 0;
    }

    observeProductRoot() {
      this.observer?.observe(this.productRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-variant-loading'],
      });
    }

    readVariantData() {
      try {
        return JSON.parse(this.querySelector('[data-createe-variant-data]')?.textContent || '[]');
      } catch (_error) {
        return [];
      }
    }

    readStyleMedia() {
      try {
        return JSON.parse(this.querySelector('[data-createe-style-media]')?.textContent || '{}');
      } catch (_error) {
        return {};
      }
    }

    getSelectedValue(optionName) {
      const input = this.productRoot.querySelector(`input[data-option-name="${optionName}"]:checked`);
      if (input instanceof HTMLInputElement) return input.value;

      const select = this.productRoot.querySelector(`select[name="options[${optionName}]"]`);
      return select instanceof HTMLSelectElement ? select.value : '';
    }

    getSelectedVariant() {
      const style = this.getSelectedValue('Style');
      const size = this.getSelectedValue('Size');
      const optimization = this.getSelectedValue('Design Optimization');

      return this.variantData.find(
        (variant) =>
          variant.style === style &&
          variant.size === size &&
          variant.optimization === optimization
      );
    }

    formatMoney(cents) {
      const value = Number(cents);
      if (!Number.isFinite(value)) return '';

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: value % 100 === 0 ? 0 : 2,
          maximumFractionDigits: 2,
        }).format(value / 100);
      } catch (_error) {
        return `${this.currency} ${(value / 100).toFixed(2)}`;
      }
    }

    getOptimizationPair() {
      const style = this.getSelectedValue('Style');
      const size = this.getSelectedValue('Size');
      const candidates = this.variantData.filter(
        (variant) => variant.style === style && (!size || variant.size === size)
      );

      const bySize = new Map();
      candidates.forEach((variant) => {
        if (!bySize.has(variant.size)) bySize.set(variant.size, {});
        const bucket = bySize.get(variant.size);
        const key = String(variant.optimization).trim().toLowerCase();
        if (key === 'no') bucket.noVariant = variant;
        if (key === 'yes') bucket.yesVariant = variant;
      });

      const preferred = size ? bySize.get(size) : null;
      const pair =
        preferred?.noVariant && preferred?.yesVariant
          ? preferred
          : Array.from(bySize.values()).find((bucket) => bucket.noVariant && bucket.yesVariant) || {};

      const feeCents =
        pair.noVariant && pair.yesVariant
          ? Number(pair.yesVariant.priceCents) - Number(pair.noVariant.priceCents)
          : null;

      return { ...pair, feeCents };
    }

    setNativeOptimization(value) {
      const input = Array.from(
        this.productRoot.querySelectorAll('input[data-option-name="Design Optimization"]')
      ).find((candidate) => candidate.value.trim().toLowerCase() === value);

      if (!(input instanceof HTMLInputElement)) return false;
      if (!input.checked) input.click();
      return true;
    }

    decorateStyleCards() {
      this.productRoot.querySelectorAll('input[data-option-name="Style"]').forEach((input) => {
        const media = this.styleMedia[input.value];
        const label = input.closest('label');
        if (!media?.src || !label) return;

        label.classList.add('br-createe-style-card');
        label.dataset.styleValue = input.value;

        if (label.querySelector('[data-createe-style-image]')) return;

        const frame = document.createElement('span');
        frame.className = 'br-createe-style-card__media';
        frame.dataset.createeStyleImage = '';

        const image = document.createElement('img');
        image.src = media.src;
        if (media.src2x) image.srcset = `${media.src} 1x, ${media.src2x} 2x`;
        image.alt = media.alt;
        image.width = 240;
        image.height = 240;
        image.loading = 'lazy';
        image.decoding = 'async';

        frame.append(image);
        input.insertAdjacentElement('afterend', frame);
      });
    }

    syncOptionAvailability() {
      const style = this.getSelectedValue('Style');
      if (!style || !this.variantData.length) return false;

      const styleVariants = this.variantData.filter(
        (variant) => variant.style === style && variant.available !== false
      );
      const validSizes = new Set(styleVariants.map((variant) => variant.size));

      this.productRoot.querySelectorAll('input[data-option-name="Style"]').forEach((input) => {
        const label = input.closest('label');
        if (!label) return;

        const exists = this.variantData.some(
          (variant) => variant.style === input.value && variant.available !== false
        );

        label.hidden = !exists;
        label.setAttribute('aria-hidden', String(!exists));
        input.disabled = !exists;

        if (exists) {
          input.dataset.optionAvailable = 'true';
          input.removeAttribute('aria-disabled');
          label.classList.add('br-createe-option--available');
          label.querySelectorAll('.variant-option__strikethrough').forEach((strike) => strike.remove());
        } else {
          label.classList.remove('br-createe-option--available');
        }
      });

      const sizeInputs = Array.from(
        this.productRoot.querySelectorAll('input[data-option-name="Size"]')
      );

      sizeInputs.forEach((input) => {
        const label = input.closest('label');
        if (!label) return;

        const valid = validSizes.has(input.value);
        label.hidden = !valid;
        label.setAttribute('aria-hidden', String(!valid));
        input.disabled = !valid;

        if (valid) {
          input.dataset.optionAvailable = 'true';
          input.removeAttribute('aria-disabled');
          label.classList.add('br-createe-option--available');
          label.querySelectorAll('.variant-option__strikethrough').forEach((strike) => strike.remove());
        } else {
          label.classList.remove('br-createe-option--available');
        }
      });

      const selectedSize = sizeInputs.find((input) => input.checked);
      if (!selectedSize || !validSizes.has(selectedSize.value)) {
        const replacement = sizeInputs.find((input) => validSizes.has(input.value));
        if (replacement && !replacement.checked) {
          replacement.click();
          return true;
        }
      }

      return false;
    }

    ensureOptimizationControl() {
      const nativeInput = this.productRoot.querySelector(
        'input[data-option-name="Design Optimization"]'
      );
      const fieldset = nativeInput?.closest('fieldset');
      if (!fieldset) return null;

      fieldset.hidden = true;
      fieldset.setAttribute('aria-hidden', 'true');
      fieldset.querySelectorAll('input').forEach((input) => {
        input.tabIndex = -1;
      });

      let control = this.productRoot.querySelector('[data-createe-optimization-control]');
      if (control) return control;

      control = document.createElement('section');
      control.className = 'br-createe-optimization-control';
      control.dataset.createeOptimizationControl = '';
      control.innerHTML = `
        <div class="br-createe-optimization-control__header">
          <h2 class="br-createe-optimization-control__heading">4. Design Optimization</h2>
          <button
            class="button-unstyled br-createe-optimization-service__learn"
            type="button"
            data-createe-optimization-learn
          >
            How it works
          </button>
        </div>
        <div class="br-createe-optimization-service" data-createe-optimization-service>
          <button
            class="button-unstyled br-createe-optimization-switch"
            type="button"
            role="switch"
            aria-checked="false"
            data-createe-optimization-toggle
          >
            <span class="br-createe-optimization-switch__mark" aria-hidden="true"></span>
            <span class="br-createe-optimization-switch__copy">
              <strong>Add Design Optimization <span data-createe-optimization-fee></span></strong>
              <span>Optional designer help before production.</span>
            </span>
          </button>
        </div>
      `;

      fieldset.insertAdjacentElement('afterend', control);
      return control;
    }

    syncOptimization() {
      const control = this.ensureOptimizationControl();
      if (!control) return;

      const isYes =
        this.getSelectedValue('Design Optimization').trim().toLowerCase() === 'yes';
      const pair = this.getOptimizationPair();
      const feeText = Number.isFinite(pair.feeCents)
        ? this.formatMoney(pair.feeCents)
        : '';

      const toggle = control.querySelector('[data-createe-optimization-toggle]');
      const service = control.querySelector('[data-createe-optimization-service]');
      const fee = control.querySelector('[data-createe-optimization-fee]');

      const isLoading = Boolean(
        this.productRoot.querySelector('product-form-component[data-variant-loading]')
      );
      toggle?.setAttribute('aria-checked', String(isYes));
      if (isLoading) toggle?.setAttribute('aria-busy', 'true');
      else toggle?.removeAttribute('aria-busy');
      if (toggle instanceof HTMLButtonElement) toggle.disabled = isLoading;
      if (service) service.dataset.selected = String(isYes);
      const nextFeeText = feeText ? `+${feeText}` : '';
      if (fee && fee.textContent !== nextFeeText) fee.textContent = nextFeeText;

      this.querySelectorAll('[data-createe-optimization-dialog-fee]').forEach((node) => {
        const nextDialogFeeText = feeText ? `+${feeText}` : 'Calculated from your selection';
        if (node.textContent !== nextDialogFeeText) node.textContent = nextDialogFeeText;
      });

      this.dataset.designOptimization = isYes ? 'yes' : 'no';
    }

    ensurePurchasePrice() {
      const addComponent = this.productRoot.querySelector(
        '.product-form-buttons add-to-cart-component'
      );
      if (!addComponent) return null;

      let host = addComponent.querySelector('[data-createe-selected-price]');
      if (!host) {
        host = document.createElement('span');
        host.className = 'br-createe-selected-price br-createe-price-breakdown';
        host.dataset.createeSelectedPrice = '';
        addComponent.prepend(host);
      }

      if (!host.querySelector('[data-createe-selected-price-value]')) {
        host.innerHTML = `
          <span class="br-createe-price-breakdown__total">
            <span>Total</span>
            <strong data-createe-selected-price-value></strong>
          </span>
        `;
      }

      return host;
    }

    syncPrice() {
      const host = this.ensurePurchasePrice();
      if (!host) return;

      const selectedVariant = this.getSelectedVariant();

      const total = host.querySelector('[data-createe-selected-price-value]');
      const nextTotal = selectedVariant?.price || 'Select options';
      if (total && total.textContent !== nextTotal) total.textContent = nextTotal;
    }

    syncVariantId() {
      const selectedVariant = this.getSelectedVariant();
      const nextId = selectedVariant?.available !== false && selectedVariant?.id ? String(selectedVariant.id) : '';

      if (nextId) this.dataset.selectedVariantId = nextId;
      else delete this.dataset.selectedVariantId;

      this.productRoot.querySelectorAll('product-form-component form input[name="id"]').forEach((input) => {
        if (input instanceof HTMLInputElement && input.value !== nextId) input.value = nextId;
      });
    }

    readSizeChartMap() {
      try {
        return JSON.parse(
          this.productRoot.querySelector('[data-createe-size-chart-map]')?.textContent || '{}'
        );
      } catch (_error) {
        return {};
      }
    }

    syncSizeChart() {
      const style = this.getSelectedValue('Style');
      if (!style) return;

      const config = this.readSizeChartMap()[style];

      this.productRoot.querySelectorAll('.br-size-chart__type').forEach((node) => {
        if (node.textContent !== style) node.textContent = style;
      });

      if (!config?.src) return;

      this.productRoot.querySelectorAll('.br-size-chart__dialog').forEach((dialog) => {
        const content = dialog.querySelector('.br-size-chart__content');
        if (!content) return;

        let media = dialog.querySelector('.br-size-chart__media');
        let image = media?.querySelector('img') || null;
        const empty = dialog.querySelector('.br-size-chart__empty');

        if (!media) {
          media = document.createElement('div');
          media.className = 'br-size-chart__media';
          image = document.createElement('img');
          image.loading = 'lazy';
          image.decoding = 'async';
          media.append(image);

          if (empty) empty.replaceWith(media);
          else content.append(media);
        }

        if (!image) {
          image = document.createElement('img');
          image.loading = 'lazy';
          image.decoding = 'async';
          media.append(image);
        }

        empty?.remove();
        const nextSrc = new URL(config.src, document.baseURI).href;
        if (image.src !== nextSrc) image.src = config.src;

        const nextAlt = `${style} size chart`;
        if (image.alt !== nextAlt) image.alt = nextAlt;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
      });
    }

    refresh() {
      const observer = this.observer;
      observer?.disconnect();

      try {
        this.decorateStyleCards();
        this.syncOptimization();
        if (this.productRoot.querySelector('product-form-component[data-variant-loading]')) return;

        const changedSize = this.syncOptionAvailability();
        if (changedSize) return;

        this.syncVariantId();
        this.syncPrice();
        this.syncSizeChart();
      } finally {
        this.observeProductRoot();
      }
    }

    scheduleRefresh = () => {
      if (this.refreshFrame) return;
      this.refreshFrame = requestAnimationFrame(() => {
        this.refreshFrame = 0;
        this.refresh();
      });
    };

    handleVariantChange = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

      const optionName = target.dataset.optionName || target.name || '';
      if (!['Style', 'Size', 'Design Optimization'].some((name) => optionName.includes(name))) return;

      this.scheduleRefresh();
    };

    handleClick = (event) => {
      const target =
        event.target instanceof Element
          ? event.target.closest(
              '[data-createe-optimization-toggle], [data-createe-optimization-learn], [data-createe-optimization-close]'
            )
          : null;

      if (!target) return;

      const dialog = this.querySelector('[data-createe-optimization-dialog]');

      if (target.matches('[data-createe-optimization-toggle]')) {
        if (this.productRoot.querySelector('product-form-component[data-variant-loading]')) return;

        const turnOn = target.getAttribute('aria-checked') !== 'true';
        target.disabled = true;
        target.setAttribute('aria-busy', 'true');
        if (this.setNativeOptimization(turnOn ? 'yes' : 'no')) {
          this.scheduleRefresh();

          if (turnOn) {
            requestAnimationFrame(() => {
              if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.showModal();
            });
          }
        } else {
          target.disabled = false;
          target.removeAttribute('aria-busy');
        }
        return;
      }

      if (!(dialog instanceof HTMLDialogElement)) return;

      if (target.matches('[data-createe-optimization-learn]')) {
        if (!dialog.open) dialog.showModal();
        return;
      }

      if (target.matches('[data-createe-optimization-close]') && dialog.open) {
        dialog.close();
      }
    };
  }

  class BrCreateeUpload extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      this.stateKey = this.dataset.formId || this.dataset.sectionId || 'createe';
      this.state = getUploadState(this.stateKey);
      this.maxFileSize = Number(this.dataset.maxFileSize || DEFAULT_MAX_FILE_SIZE);
      this.inputs = Array.from(this.querySelectorAll('[data-artwork-input]'));
      this.guidelinesDialog = this.querySelector('[data-guidelines-dialog]');
      this.dialog = this.querySelector('[data-crop-dialog]');
      this.canvas = this.querySelector('[data-crop-canvas]');
      this.cropStatus = this.querySelector('[data-crop-status]');
      this.cropHelp = this.querySelector('.br-createe-crop__header p');
      this.uploadStatus = this.querySelector('[data-upload-status]');
      this.activeIndex = null;
      this.decoded = null;
      this.pointer = null;
      this.returnFocus = null;
      this.guidelinesReturnFocus = null;

      if (this.cropHelp) {
        this.cropHelp.textContent =
          'Drag to move or resize. Use arrow keys to move; use Ctrl or Command + arrow keys to resize. Hold Shift for larger steps.';
      }

      this.inputs.forEach((input) => {
        input.addEventListener('change', this.handleFileChange, { signal: this.abortController.signal });
      });

      this.querySelector('[data-add-image]')?.addEventListener('click', this.handleAddImage, {
        signal: this.abortController.signal,
      });
      this.querySelector('[data-guidelines-open]')?.addEventListener('click', this.openGuidelines, {
        signal: this.abortController.signal,
      });
      this.querySelectorAll('[data-guidelines-close], [data-guidelines-got-it]').forEach((button) => {
        button.addEventListener('click', this.closeGuidelines, { signal: this.abortController.signal });
      });
      this.guidelinesDialog?.addEventListener('cancel', this.handleGuidelinesCancel, {
        signal: this.abortController.signal,
      });
      this.guidelinesDialog?.addEventListener('click', this.handleGuidelinesClick, {
        signal: this.abortController.signal,
      });
      this.guidelinesDialog?.addEventListener('keydown', this.trapGuidelinesFocus, {
        signal: this.abortController.signal,
      });
      this.guidelinesDialog?.addEventListener('close', this.handleGuidelinesClosed, {
        signal: this.abortController.signal,
      });
      this.querySelectorAll('[data-edit-crop]').forEach((button) => {
        button.addEventListener('click', this.handleEditCrop, { signal: this.abortController.signal });
      });
      this.querySelectorAll('[data-remove-image]').forEach((button) => {
        button.addEventListener('click', this.handleRemoveImage, { signal: this.abortController.signal });
      });
      this.querySelector('[data-crop-cancel]')?.addEventListener('click', this.cancelActive, {
        signal: this.abortController.signal,
      });
      this.querySelector('[data-crop-reset]')?.addEventListener('click', this.resetActive, {
        signal: this.abortController.signal,
      });
      this.querySelector('[data-crop-save]')?.addEventListener('click', this.saveActive, {
        signal: this.abortController.signal,
      });
      this.canvas?.addEventListener('pointerdown', this.handlePointerDown, { signal: this.abortController.signal });
      this.canvas?.addEventListener('pointermove', this.handlePointerMove, { signal: this.abortController.signal });
      this.canvas?.addEventListener('pointerup', this.handlePointerUp, { signal: this.abortController.signal });
      this.canvas?.addEventListener('pointercancel', this.handlePointerUp, { signal: this.abortController.signal });
      this.canvas?.addEventListener('keydown', this.handleCanvasKeydown, { signal: this.abortController.signal });
      this.dialog?.addEventListener('cancel', this.handleDialogCancel, { signal: this.abortController.signal });

      this.bindForm();
      this.renderSlots();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.formAbortController?.abort();
      this.cleanupDecoded();
      if (this.dialog?.open) this.dialog.close();
      if (this.guidelinesDialog?.open) this.guidelinesDialog.close();
      document.documentElement.classList.remove('br-createe-modal-open');
      this.abortController = null;
      this.formAbortController = null;
    }

    openGuidelines = (event) => {
      if (!this.guidelinesDialog || this.guidelinesDialog.open) return;
      this.guidelinesReturnFocus = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
      document.documentElement.classList.add('br-createe-modal-open');
      this.guidelinesDialog.showModal();
      this.guidelinesDialog.querySelector('[data-guidelines-close]')?.focus();
    };

    closeGuidelines = () => {
      if (this.guidelinesDialog?.open) this.guidelinesDialog.close();
    };

    handleGuidelinesCancel = (event) => {
      event.preventDefault();
      this.closeGuidelines();
    };

    handleGuidelinesClick = (event) => {
      if (event.target === this.guidelinesDialog) this.closeGuidelines();
    };

    handleGuidelinesClosed = () => {
      document.documentElement.classList.remove('br-createe-modal-open');
      this.guidelinesReturnFocus?.focus();
      this.guidelinesReturnFocus = null;
    };

    trapGuidelinesFocus = (event) => {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        this.guidelinesDialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.hidden && element.getClientRects().length > 0);
      if (focusable.length < 1) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    bindForm() {
      const form = document.getElementById(this.dataset.formId || '');
      if (!(form instanceof HTMLFormElement)) return;

      this.formAbortController?.abort();
      this.formAbortController = new AbortController();
      this.form = form;
      this.form.addEventListener('submit', this.handleSubmitCapture, {
        capture: true,
        signal: this.formAbortController.signal,
      });
      this.form.addEventListener('formdata', this.handleFormData, { signal: this.formAbortController.signal });
      document.addEventListener('pointerdown', this.handleAddIntentCapture, {
        capture: true,
        signal: this.formAbortController.signal,
      });
      document.addEventListener('click', this.handleAddIntentCapture, {
        capture: true,
        signal: this.formAbortController.signal,
      });
    }

    setUploadStatus(message, isError = false) {
      if (!this.uploadStatus) return;
      this.uploadStatus.textContent = message;
      this.uploadStatus.classList.toggle('br-createe__status--error', Boolean(message) && isError);
    }

    setCropStatus(message, isError = false) {
      if (!this.cropStatus) return;
      this.cropStatus.textContent = message;
      this.cropStatus.classList.toggle('br-createe__status--error', Boolean(message) && isError);
    }

    validateFile(file) {
      if (!ACCEPTED_TYPES.has(file.type)) return 'Choose a JPEG, PNG or WEBP image.';
      if (file.size > this.maxFileSize) return 'Each artwork file must be 10 MB or smaller.';
      if (file.size === 0) return 'Choose a file that is not empty.';
      return '';
    }

    renderSlots() {
      const savedCount = this.state.slots.filter((slot) => slot.cropped && slot.status === 'saved').length;
      const occupiedCount = this.state.slots.filter((slot) => slot.original).length;

      this.inputs.forEach((input, index) => {
        const slot = this.state.slots[index];
        const slotElement = this.querySelector(`[data-upload-slot][data-slot-index="${index}"]`);
        const preview = slotElement?.querySelector('[data-slot-preview]');
        const filename = slotElement?.querySelector('[data-slot-filename]');
        const status = slotElement?.querySelector('[data-slot-status]');
        const editButton = slotElement?.querySelector('[data-edit-crop]');
        const removeButton = slotElement?.querySelector('[data-remove-image]');

        if (slotElement) slotElement.hidden = !slot.original;
        if (preview) {
          preview.hidden = !slot.previewUrl;
          if (slot.previewUrl) preview.src = slot.previewUrl;
          else preview.removeAttribute('src');
        }
        if (filename) filename.textContent = slot.original?.name || '';
        if (status) {
          status.textContent = slot.status === 'saved' ? 'Saved' : slot.status === 'error' ? 'Error' : 'Needs crop';
          status.dataset.state = slot.status;
        }
        if (editButton) {
          editButton.textContent = 'Edit crop';
          editButton.hidden = slot.status === 'error';
        }
        if (removeButton) removeButton.hidden = !slot.original;

        input.required = index === 0 && savedCount === 0;
        input.setCustomValidity(slot.error || '');
      });

      const addButton = this.querySelector('[data-add-image]');
      if (addButton) {
        addButton.hidden = occupiedCount >= 4;
        addButton.disabled = occupiedCount >= 4;
      }
    }

    handleAddImage = () => {
      const index = this.state.slots.findIndex((slot) => !slot.original);
      if (index < 0) {
        this.setUploadStatus('You can upload up to four images.');
        return;
      }
      this.inputs[index]?.click();
    };

    handleFileChange = (event) => {
      const input = event.currentTarget;
      const index = Number(input.dataset.slotIndex);
      const file = input.files?.[0];
      if (!file || !Number.isInteger(index)) return;

      const slot = this.state.slots[index];
      revokeUrl(slot.previewUrl);
      slot.original = file;
      slot.cropped = null;
      slot.previewUrl = '';
      slot.crop = createDefaultCrop();
      slot.error = this.validateFile(file);
      slot.status = slot.error ? 'error' : 'needs-crop';
      this.renderSlots();

      if (slot.error) {
        this.setUploadStatus(slot.error, true);
        input.reportValidity();
        return;
      }

      this.setUploadStatus(`Image ${index + 1} needs crop.`);
      this.openCrop(index, input);
    };

    handleRemoveImage = (event) => {
      const slotElement = event.currentTarget.closest('[data-upload-slot]');
      const index = Number(slotElement?.dataset.slotIndex);
      if (!Number.isInteger(index)) return;
      this.clearSlot(index, `Image ${index + 1} removed.`);
    };

    handleEditCrop = (event) => {
      const slotElement = event.currentTarget.closest('[data-upload-slot]');
      const index = Number(slotElement?.dataset.slotIndex);
      if (!Number.isInteger(index)) return;
      if (this.state.slots[index]?.error) return;
      this.openCrop(index, event.currentTarget);
    };

    async decodeFile(file) {
      if ('createImageBitmap' in window) {
        try {
          const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
          return {
            source: bitmap,
            width: bitmap.width,
            height: bitmap.height,
            close: () => bitmap.close(),
            method: 'createImageBitmap-from-image',
          };
        } catch (_error) {
          // Continue to the Safari-compatible image decode path.
        }
      }

      const objectUrl = URL.createObjectURL(file);
      const image = new Image();
      image.decoding = 'async';
      image.src = objectUrl;
      await image.decode();
      return {
        source: image,
        width: image.naturalWidth,
        height: image.naturalHeight,
        close: () => {
          image.removeAttribute('src');
          revokeUrl(objectUrl);
        },
        method: 'html-image-decode',
      };
    }

    async openCrop(index, returnFocus) {
      const slot = this.state.slots[index];
      if (!slot?.original || slot.error) return;

      this.cleanupDecoded();
      this.activeIndex = index;
      this.returnFocus = returnFocus instanceof HTMLElement ? returnFocus : null;
      this.openingCrop = { ...slot.crop };
      this.activeHadSavedCrop = Boolean(slot.cropped && slot.status === 'saved');
      this.setCropStatus('Preparing image.');
      if (!this.dialog.open) this.dialog.showModal();

      try {
        this.decoded = await this.decodeFile(slot.original);
        if (this.activeIndex !== index) {
          this.cleanupDecoded();
          return;
        }
        this.configureCropCanvas();
        this.renderCrop();
        this.setCropStatus('Image ready.');
        this.canvas.focus();
      } catch (_error) {
        slot.status = 'error';
        slot.error = 'This image could not be decoded. Choose another file.';
        this.renderSlots();
        this.setCropStatus(slot.error, true);
      }
    }

    cleanupDecoded() {
      this.decoded?.close?.();
      this.decoded = null;
    }

    configureCropCanvas() {
      if (!this.canvas || !this.decoded) return;
      const scale = Math.min(900 / this.decoded.width, 650 / this.decoded.height);
      this.canvas.width = Math.max(1, Math.round(this.decoded.width * scale));
      this.canvas.height = Math.max(1, Math.round(this.decoded.height * scale));
    }

    getCropRect() {
      if (!this.decoded || this.activeIndex === null) return null;
      const slot = this.state.slots[this.activeIndex];
      return {
        x: slot.crop.x * this.decoded.width,
        y: slot.crop.y * this.decoded.height,
        width: slot.crop.width * this.decoded.width,
        height: slot.crop.height * this.decoded.height,
      };
    }

    renderCrop() {
      if (!this.canvas || !this.decoded || this.activeIndex === null) return;
      const crop = this.state.slots[this.activeIndex].crop;
      const context = this.canvas.getContext('2d', { alpha: true });
      context.clearRect(0, 0, this.canvas.width, this.canvas.height);
      context.drawImage(this.decoded.source, 0, 0, this.canvas.width, this.canvas.height);

      const selection = {
        x: crop.x * this.canvas.width,
        y: crop.y * this.canvas.height,
        width: crop.width * this.canvas.width,
        height: crop.height * this.canvas.height,
      };
      const right = selection.x + selection.width;
      const bottom = selection.y + selection.height;
      context.fillStyle = 'rgba(20, 20, 20, 0.18)';
      context.fillRect(0, 0, this.canvas.width, selection.y);
      context.fillRect(0, bottom, this.canvas.width, this.canvas.height - bottom);
      context.fillRect(0, selection.y, selection.x, selection.height);
      context.fillRect(right, selection.y, this.canvas.width - right, selection.height);

      const brand = getComputedStyle(this.canvas).getPropertyValue('--br-color-brand-strong').trim() || '#209B87';
      const canvasBounds = this.canvas.getBoundingClientRect();
      const displayScale = this.canvas.width / Math.max(1, canvasBounds.width);
      context.strokeStyle = 'rgba(255, 255, 255, 0.92)';
      context.lineWidth = Math.max(3, 4 * displayScale);
      context.strokeRect(selection.x, selection.y, selection.width, selection.height);
      context.strokeStyle = brand;
      context.lineWidth = Math.max(2, 2 * displayScale);
      context.strokeRect(selection.x, selection.y, selection.width, selection.height);

      context.save();
      context.strokeStyle = 'rgba(255, 255, 255, 0.82)';
      context.lineWidth = Math.max(1, displayScale);
      [1 / 3, 2 / 3].forEach((part) => {
        const guideX = selection.x + selection.width * part;
        const guideY = selection.y + selection.height * part;
        context.beginPath();
        context.moveTo(guideX, selection.y);
        context.lineTo(guideX, bottom);
        context.moveTo(selection.x, guideY);
        context.lineTo(right, guideY);
        context.stroke();
      });
      context.restore();

      this.cropHandles = [
        ['nw', selection.x, selection.y],
        ['n', selection.x + selection.width / 2, selection.y],
        ['ne', right, selection.y],
        ['e', right, selection.y + selection.height / 2],
        ['se', right, bottom],
        ['s', selection.x + selection.width / 2, bottom],
        ['sw', selection.x, bottom],
        ['w', selection.x, selection.y + selection.height / 2],
      ];
      const handleRadius = 9 * displayScale;
      this.cropHandles.forEach(([, x, y]) => {
        context.beginPath();
        context.arc(x, y, handleRadius, 0, Math.PI * 2);
        context.fillStyle = '#ffffff';
        context.fill();
        context.strokeStyle = brand;
        context.lineWidth = Math.max(2, 2 * displayScale);
        context.stroke();
      });
    }

    pointerToCanvas(event) {
      const bounds = this.canvas.getBoundingClientRect();
      return {
        x: (event.clientX - bounds.left) * (this.canvas.width / Math.max(1, bounds.width)),
        y: (event.clientY - bounds.top) * (this.canvas.height / Math.max(1, bounds.height)),
        bounds,
      };
    }

    getPointerTarget(point) {
      const hitX = 28 * (this.canvas.width / Math.max(1, point.bounds.width));
      const hitY = 28 * (this.canvas.height / Math.max(1, point.bounds.height));
      const handle = this.cropHandles?.find(([, x, y]) => Math.abs(point.x - x) <= hitX && Math.abs(point.y - y) <= hitY);
      if (handle) return handle[0];

      const crop = this.state.slots[this.activeIndex].crop;
      const x = crop.x * this.canvas.width;
      const y = crop.y * this.canvas.height;
      const right = x + crop.width * this.canvas.width;
      const bottom = y + crop.height * this.canvas.height;
      return point.x >= x && point.x <= right && point.y >= y && point.y <= bottom ? 'move' : 'new';
    }

    handlePointerDown = (event) => {
      if (this.activeIndex === null || !this.decoded) return;
      const point = this.pointerToCanvas(event);
      const target = this.getPointerTarget(point);
      if (!target) return;
      event.preventDefault();
      this.pointer = {
        id: event.pointerId,
        x: point.x,
        y: point.y,
        target,
        didMove: false,
        crop: { ...this.state.slots[this.activeIndex].crop },
      };
      this.canvas.setPointerCapture(event.pointerId);
    };

    handlePointerMove = (event) => {
      if (!this.pointer) {
        if (this.activeIndex === null || !this.decoded) return;
        const target = this.getPointerTarget(this.pointerToCanvas(event));
        const cursors = {
          move: 'move',
          n: 'ns-resize',
          s: 'ns-resize',
          e: 'ew-resize',
          w: 'ew-resize',
          ne: 'nesw-resize',
          sw: 'nesw-resize',
          nw: 'nwse-resize',
          se: 'nwse-resize',
          new: 'crosshair',
        };
        this.canvas.style.cursor = cursors[target] || 'crosshair';
        return;
      }
      if (this.pointer.id !== event.pointerId || this.activeIndex === null) return;
      event.preventDefault();
      const slot = this.state.slots[this.activeIndex];
      const point = this.pointerToCanvas(event);
      const dx = (point.x - this.pointer.x) / this.canvas.width;
      const dy = (point.y - this.pointer.y) / this.canvas.height;
      const start = this.pointer.crop;

      this.pointer.didMove = this.pointer.didMove || Math.abs(dx) >= 0.005 || Math.abs(dy) >= 0.005;

      if (this.pointer.target === 'move') {
        slot.crop.x = clamp(start.x + dx, 0, 1 - start.width);
        slot.crop.y = clamp(start.y + dy, 0, 1 - start.height);
      } else if (this.pointer.target === 'new') {
        const startX = clamp(this.pointer.x / this.canvas.width, 0, 1);
        const startY = clamp(this.pointer.y / this.canvas.height, 0, 1);
        const endX = clamp(point.x / this.canvas.width, 0, 1);
        const endY = clamp(point.y / this.canvas.height, 0, 1);
        const left = Math.min(startX, endX);
        const top = Math.min(startY, endY);
        const width = Math.abs(endX - startX);
        const height = Math.abs(endY - startY);
        if (width >= MIN_CROP_SIZE && height >= MIN_CROP_SIZE) {
          slot.crop = { x: left, y: top, width, height };
        }
      } else {
        let left = start.x;
        let top = start.y;
        let right = start.x + start.width;
        let bottom = start.y + start.height;
        if (this.pointer.target.includes('w')) left = clamp(start.x + dx, 0, right - MIN_CROP_SIZE);
        if (this.pointer.target.includes('e')) right = clamp(start.x + start.width + dx, left + MIN_CROP_SIZE, 1);
        if (this.pointer.target.includes('n')) top = clamp(start.y + dy, 0, bottom - MIN_CROP_SIZE);
        if (this.pointer.target.includes('s')) bottom = clamp(start.y + start.height + dy, top + MIN_CROP_SIZE, 1);
        slot.crop = { x: left, y: top, width: right - left, height: bottom - top };
      }
      this.renderCrop();
    };

    handlePointerUp = (event) => {
      if (!this.pointer || this.pointer.id !== event.pointerId) return;
      if (this.pointer.target === 'new' && !this.pointer.didMove && this.activeIndex !== null) {
        const slot = this.state.slots[this.activeIndex];
        const point = this.pointerToCanvas(event);
        const centerX = clamp(point.x / this.canvas.width, 0, 1);
        const centerY = clamp(point.y / this.canvas.height, 0, 1);
        slot.crop.x = clamp(centerX - slot.crop.width / 2, 0, 1 - slot.crop.width);
        slot.crop.y = clamp(centerY - slot.crop.height / 2, 0, 1 - slot.crop.height);
        this.renderCrop();
      }
      if (this.canvas.hasPointerCapture(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
      this.pointer = null;
      this.setCropStatus('Crop updated. Drag inside to move or use a handle to resize.');
    };

    handleCanvasKeydown = (event) => {
      if (this.activeIndex === null) return;
      const moves = {
        ArrowLeft: [-1, 0],
        ArrowRight: [1, 0],
        ArrowUp: [0, -1],
        ArrowDown: [0, 1],
      };
      if (!moves[event.key]) return;
      event.preventDefault();
      const amount = event.shiftKey ? 0.05 : 0.01;
      const slot = this.state.slots[this.activeIndex];

      if (event.ctrlKey || event.metaKey) {
        if (event.key === 'ArrowLeft') {
          slot.crop.width = clamp(slot.crop.width - amount, MIN_CROP_SIZE, 1 - slot.crop.x);
        } else if (event.key === 'ArrowRight') {
          slot.crop.width = clamp(slot.crop.width + amount, MIN_CROP_SIZE, 1 - slot.crop.x);
        } else if (event.key === 'ArrowUp') {
          slot.crop.height = clamp(slot.crop.height - amount, MIN_CROP_SIZE, 1 - slot.crop.y);
        } else if (event.key === 'ArrowDown') {
          slot.crop.height = clamp(slot.crop.height + amount, MIN_CROP_SIZE, 1 - slot.crop.y);
        }
        this.renderCrop();
        this.setCropStatus('Crop size updated.');
        return;
      }

      slot.crop.x = clamp(slot.crop.x + moves[event.key][0] * amount, 0, 1 - slot.crop.width);
      slot.crop.y = clamp(slot.crop.y + moves[event.key][1] * amount, 0, 1 - slot.crop.height);
      this.renderCrop();
      this.setCropStatus('Crop position updated.');
    };

    resetActive = () => {
      if (this.activeIndex === null) return;
      const slot = this.state.slots[this.activeIndex];
      slot.crop = createDefaultCrop();
      this.renderCrop();
      this.setCropStatus('Crop reset to a centered selection.');
      this.canvas.focus();
    };

    handleDialogCancel = (event) => {
      event.preventDefault();
      this.cancelActive();
    };

    cancelActive = () => {
      if (this.activeIndex === null) return;
      const index = this.activeIndex;
      if (this.activeHadSavedCrop) {
        this.state.slots[index].crop = { ...this.openingCrop };
        this.cleanupDecoded();
        this.activeIndex = null;
        this.dialog.close();
        this.setUploadStatus(`Image ${index + 1} crop changes cancelled.`);
        this.returnFocus?.focus();
        this.returnFocus = null;
        return;
      }
      this.clearSlot(index, `Image ${index + 1} removed.`);
    };

    clearSlot(index, message) {
      const slot = this.state.slots[index];
      revokeUrl(slot.previewUrl);
      this.state.slots[index] = {
        original: null,
        cropped: null,
        previewUrl: '',
        status: 'empty',
        error: '',
        crop: createDefaultCrop(),
      };
      if (this.inputs[index]) this.inputs[index].value = '';
      this.cleanupDecoded();
      this.activeIndex = null;
      this.openingCrop = null;
      this.activeHadSavedCrop = false;
      if (this.dialog?.open) this.dialog.close();
      this.renderSlots();
      this.setUploadStatus(message);
      this.querySelector('[data-add-image]')?.focus();
    }

    canvasToBlob(canvas, type, quality) {
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Canvas export returned no file.'));
        }, type, quality);
      });
    }

    hasAlpha(canvas) {
      const data = canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, canvas.width, canvas.height).data;
      for (let index = 3; index < data.length; index += 4) {
        if (data[index] < 255) return true;
      }
      return false;
    }

    saveActive = async () => {
      if (this.activeIndex === null || !this.decoded) return;
      const index = this.activeIndex;
      const slot = this.state.slots[index];
      const rect = this.getCropRect();
      const saveButton = this.querySelector('[data-crop-save]');
      saveButton.disabled = true;
      saveButton.setAttribute('aria-busy', 'true');
      this.setCropStatus('Saving crop.');

      const scale = Math.min(1, MAX_OUTPUT_EDGE / Math.max(rect.width, rect.height));
      const width = Math.max(1, Math.round(rect.width * scale));
      const height = Math.max(1, Math.round(rect.height * scale));
      const output = document.createElement('canvas');
      output.width = width;
      output.height = height;

      try {
        const context = output.getContext('2d', { alpha: true });
        context.drawImage(this.decoded.source, rect.x, rect.y, rect.width, rect.height, 0, 0, width, height);
        const transparent = this.hasAlpha(output);
        const type = transparent ? 'image/png' : 'image/jpeg';
        const extension = transparent ? 'png' : 'jpg';
        const blob = await this.canvasToBlob(output, type, transparent ? undefined : JPEG_QUALITY);
        if (blob.size > this.maxFileSize) throw new Error('The saved crop is larger than 10 MB. Zoom in or choose another image.');

        const baseName = slot.original.name.replace(/\.[^.]+$/, '') || `artwork-${index + 1}`;
        const cropped = new File([blob], `${baseName}-cropped.${extension}`, {
          type,
          lastModified: Date.now(),
        });

        revokeUrl(slot.previewUrl);
        slot.cropped = cropped;
        slot.previewUrl = URL.createObjectURL(cropped);
        slot.status = 'saved';
        slot.error = '';
        this.inputs[index]?.setCustomValidity('');
        this.cleanupDecoded();
        this.activeIndex = null;
        this.openingCrop = null;
        this.activeHadSavedCrop = false;
        this.dialog.close();
        this.renderSlots();
        this.setUploadStatus(`Image ${index + 1} saved as ${type}.`);
        this.querySelector(`[data-upload-slot][data-slot-index="${index}"] [data-edit-crop]`)?.focus();
      } catch (error) {
        slot.status = 'error';
        slot.error = error instanceof Error ? error.message : 'The crop could not be saved.';
        this.renderSlots();
        this.setCropStatus(slot.error, true);
      } finally {
        output.width = 0;
        output.height = 0;
        saveButton.disabled = false;
        saveButton.removeAttribute('aria-busy');
      }
    };

    getBaseColorControl() {
      return Array.from(document.querySelectorAll('[data-br-createe-base-color]')).find(
        (element) => element.dataset.formId === this.dataset.formId
      );
    }

    getExpectedVariantId() {
      const productRoot = this.closest('.product-information') || this.closest('.shopify-section') || document;
      const summary = productRoot.querySelector('[data-br-createe-design-summary]');
      return summary instanceof HTMLElement ? summary.dataset.selectedVariantId || '' : '';
    }

    getValidationMessage() {
      if (this.form?.closest('product-form-component')?.hasAttribute('data-variant-loading')) {
        return 'Please wait while your selection updates.';
      }

      const expectedVariantId = this.getExpectedVariantId();
      const variantInput = this.form?.querySelector('input[name="id"]');
      if (expectedVariantId && variantInput instanceof HTMLInputElement && variantInput.value !== expectedVariantId) {
        variantInput.value = expectedVariantId;
      }
      const currentVariantId = variantInput instanceof HTMLInputElement ? variantInput.value : '';
      if (!currentVariantId || (expectedVariantId && currentVariantId !== expectedVariantId)) {
        return 'Please wait while your selection updates.';
      }

      const selectedSlots = this.state.slots.filter((slot) => slot.original);
      const savedSlots = selectedSlots.filter((slot) => slot.cropped && slot.status === 'saved');
      if (savedSlots.length < 1) return 'Add and save at least one image.';
      if (selectedSlots.some((slot) => !slot.cropped || slot.status !== 'saved')) {
        return 'Save or cancel each image before adding this item to your cart.';
      }

      const baseColor = this.getBaseColorControl();
      if (!(baseColor instanceof BrCreateeBaseColor) || !baseColor.validate()) {
        return 'Choose a base color before adding this item to your cart.';
      }

      return '';
    }

    blockEvent(event, message) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.setUploadStatus(message, true);
      if (message.startsWith('Add and save')) this.querySelector('[data-add-image]')?.focus();
    }

    handleSubmitCapture = (event) => {
      const message = this.getValidationMessage();
      if (message) this.blockEvent(event, message);
    };

    handleAddIntentCapture = (event) => {
      const button = event.target instanceof Element ? event.target.closest('button') : null;
      if (!button) return;
      const isMainAdd = button.form === this.form && button.matches('[type="submit"][name="add"]');
      const isStickyAdd = Boolean(button.closest('sticky-add-to-cart'));
      if (!isMainAdd && !isStickyAdd) return;

      const isLoading = this.form?.closest('product-form-component')?.hasAttribute('data-variant-loading');
      if (event.type === 'pointerdown' && !isLoading) return;

      const message = this.getValidationMessage();
      if (message) this.blockEvent(event, message);
    };

    handleFormData = (event) => {
      const expectedVariantId = this.getExpectedVariantId();
      if (expectedVariantId) event.formData.set('id', expectedVariantId);

      const baseColor = this.getBaseColorControl();
      const baseColorValue = baseColor instanceof BrCreateeBaseColor ? baseColor.getValue() : '';
      if (baseColorValue) event.formData.set('properties[Base Color]', baseColorValue);
      else event.formData.delete('properties[Base Color]');

      this.state.slots.forEach((slot, index) => {
        const propertyName = `properties[Artwork ${index + 1}]`;
        if (slot.cropped && slot.status === 'saved') {
          event.formData.set(propertyName, slot.cropped, slot.cropped.name);
        } else {
          event.formData.delete(propertyName);
        }
      });
    };
  }

  if (!customElements.get('br-createe-reels')) customElements.define('br-createe-reels', BrCreateeReels);
  if (!customElements.get('br-createe-base-color')) customElements.define('br-createe-base-color', BrCreateeBaseColor);
  if (!customElements.get('br-createe-design-summary')) {
    customElements.define('br-createe-design-summary', BrCreateeDesignSummary);
  }
  if (!customElements.get('br-createe-upload')) customElements.define('br-createe-upload', BrCreateeUpload);

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target;
    section?.querySelectorAll?.('[data-br-createe-upload]').forEach((element) => {
      const key = element.dataset.formId || element.dataset.sectionId;
      const state = uploadStates.get(key);
      state?.slots.forEach((slot) => revokeUrl(slot.previewUrl));
      uploadStates.delete(key);
      baseColorStates.delete(key);
    });
  });
})();
